import type {
    PatentProfile,
    PatentProfileByKeywordRequest,
    PatentProfileByKeywordResponse,
    PatentListRequest,
    PatentListResponse,
    PatentCountResponse,
    PatentListResponseV2,
    PatentListRequestV2,
    PatentProfileWithFamilyResponse,
    PatentProfileWithFamily,
} from '../types';

export const PatentApi = Symbol('PatentApi');

/**
 * 根据专利号获取专利概要信息 API
 */
export interface PatentApi {
    /**
     * 使用DataLoader获取单个专利详情（带缓存和去重）
     * @param patentId 专利ID
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<PatentProfile | null>
     */
    getPatentProfile: (patentId: string, lang?: string) => Promise<PatentProfile | null>;

    /**
     * 使用DataLoader批量获取多个专利详情（带缓存和去重）
     * @param patentIds 专利ID数组
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<Record<string, PatentProfile>>
     */
    getPatentProfiles: (patentIds: string[], lang?: string) => Promise<Record<string, PatentProfile>>;

    /**
     * 使用DataLoader获取单个专利详情及同族信息（带缓存和去重）
     * @param patentId 专利ID
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<PatentProfileWithFamily | null>
     */
    getPatentProfileWithFamily: (patentId: string, lang?: string) => Promise<PatentProfileWithFamily | null>;

    /**
     * 批量获取专利详情及同族信息
     * @param patentIds 专利ID数组
     * @param lang 语言代码（可选，默认使用当前语言）
     * @returns Promise<PatentProfileWithFamilyResponse>
     */
    getPatentProfilesWithFamily: (patentIds: string[], lang?: string) => Promise<PatentProfileWithFamilyResponse>;

    /**
     * 清空DataLoader缓存
     * @param lang 可选，指定清空某个语言的缓存，不指定则清空所有语言
     */
    clearCache: (lang?: string) => void;

    /**
     * 清空特定专利ID的缓存
     * @param patentId 专利ID
     * @param lang 可选，指定清空某个语言的缓存，不指定则清空所有语言
     */
    clearPatentCache: (patentId: string, lang?: string) => void;

    /**
     * 根据关键词获取专利概要信息
     * @param data
     * @returns
     */
    getPatentProfileByKeyword: (data: PatentProfileByKeywordRequest) => Promise<PatentProfileByKeywordResponse>;

    /**
     * @deprecated 使用 getPatentListV2 代替
     * 获取专利列表
     * @param data 专利列表查询请求参数
     * @returns 专利列表响应结果
     */
    getPatentList(data: PatentListRequest): Promise<PatentListResponse>;
    /**
     * 获取专利列表
     * @param data 专利列表查询请求参数
     * @returns 专利列表响应结果
     */
    getPatentListV2(data: PatentListRequestV2): Promise<PatentListResponseV2>;

    /**
     * 获取专利数量
     * @param data 专利列表查询请求参数
     * @returns 专利数量响应结果
     */
    getPatentCount: (data: PatentListRequestV2) => Promise<PatentCountResponse>;

    /**
     * 根据申请号批量查询专利ID
     * @param apnos 申请号数组
     * @returns 申请号到专利ID列表的映射
     */
    getPatentIdsByApnos: (apnos: string[]) => Promise<Record<string, string[]>>;
}
